import moment from 'moment'
import AuthMixin from '@/components/mixins/auth-mixin'
import { useGroupStore } from '@base-shared/stores/group';

export default {
  name: 'invoiceSummary',
  inject: [
    '$currentUser',
    '$utils',
    '$api',
    '$apiError',
    '$signedInUserInfo'
  ],
  props: {
    accessorComponentProp: {
      type: String,
      default () { return 'purchasePlan' }
    },
    showCancelButtonProp: {
      type: Boolean,
      default () { return false }
    }
  },
  watch: {
    showCancelButtonProp (newVal) {
      this.showCancelButton = newVal
    }
  },
  mixins: [AuthMixin],
  data () {
    return {
      hasTermsOfSale: CONFIG.HAS_TERMS_OF_SALE,
      accessorComponent: this.accessorComponentProp,
      accessorComponentOptions: { purchasePlan: 'purchasePlan', setup: 'setup' },
      error: '',
      panelHeaderIconClass: { icon: true },
      panelHeaderTitle: this.$t('label.review'),
      planName: '',
      invoiceSummary: null,
      showCancelButton: this.showCancelButtonProp
    }
  },
  setup () {
    // Expose the user store to the Options API context.
    return { groupStore: useGroupStore() };
  },
  methods: {
    emitEvent (action, data) {
      if (action !== null || action.length > 0) {
        this.$emit('purchase-summary-change', { action, payload: { data } })
      }
    },
    setDefaultData () {
      this.panelHeaderTitle = this.$t('label.review')

      if (this.accessorComponent === this.accessorComponentOptions.purchasePlan) {
        this.planName = this.$route.params.plan
      }

      if (this.accessorComponent === this.accessorComponentOptions.setup) {
        this.panelHeaderTitle = this.$t('label.plan', 1)
      }

      /* Get Invoice summary
        - If this component access by purchase plan and user has no player then charge as 1 player
        - If this component access by setup then charge the upcoming player
       */
      if (this.accessorComponent === this.accessorComponentOptions.purchasePlan) {
        const getPlayersRequest = () => {
          this.$utils.mainLoader(true)
          this.$api.get(`${CONFIG.API_BASE_URL}/player`)
            .then((response) => {
              const countPlayers = (response.data.players) ? response.data.players.length : 0
              let getInvoiceParams = {}

              if (countPlayers === 0) {
                getInvoiceParams = { addQueryParams: '?newPlayer=PLAYER' }
              }
              this.getInvoiceSummary(getInvoiceParams)
            })
            .catch((error) => {
              this.$utils.mainLoader(false)
              this.$apiError.handleAPIRequestError(error, { originalRequest: () => { getPlayersRequest() }, showDialogNotification: true })
            })
        }
        getPlayersRequest()
      } else if (this.accessorComponent === this.accessorComponentOptions.setup) {
        this.getInvoiceSummary({ addQueryParams: '?newPlayer=NEW PLAYER' })
      } else {
        this.getInvoiceSummary()
      }
    },
    getInvoiceSummary (params) {
      const apiRequest = () => {
        let apiRoute = '/invoice/summary'

        if (typeof (params) === 'object') {
          if (params.addQueryParams) {
            apiRoute += `${params.addQueryParams}`
          }
        }

        this.$utils.mainLoader(true)
        this.$api.get(`${CONFIG.API_BASE_URL}${apiRoute}`)
          .then((response) => {
            this.invoiceSummary = this.filterInvoiceSummaryData(response.data)
            this.$utils.mainLoader(false)
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, { originalRequest: () => { apiRequest() }, showDialogNotification: true })
          })
      }
      apiRequest()
    },
    /**
     * Filter invoice summary data for UI
     * @param {Object} invoiceSummarySrc
     */
    filterInvoiceSummaryData (invoiceSummarySrc) {
      const invoiceSummary = this.$utils.clone(invoiceSummarySrc)
      invoiceSummary._edit = this.$utils.clone(invoiceSummarySrc)

      const calcSubtotal = invoiceSummary.subtotal / 100
      const calcVat = invoiceSummary.vat / 100
      const calcVatInPercent = (invoiceSummary.vatPercent) ? 100 * invoiceSummary.vatPercent : Math.round((100 * (calcVat) / (calcSubtotal)) * 10) / 10
      const calcAmount = invoiceSummary.amount / 100

      invoiceSummary._edit.amount = calcAmount
      invoiceSummary._edit.amountWithCurrency = `${invoiceSummary.currency} ${calcAmount.toFixed(2)}`
      invoiceSummary._edit.date = moment(invoiceSummary.date).format('MMM DD YYYY')
      invoiceSummary._edit.subtotal = calcSubtotal
      invoiceSummary._edit.subtotalWithCurrency = `${invoiceSummary.currency} ${calcSubtotal.toFixed(2)}`
      invoiceSummary._edit.vat = calcVat
      invoiceSummary._edit.vatWithCurrency = `${invoiceSummary.currency} ${calcVat.toFixed(2)}`
      invoiceSummary._edit.vatInPercent = calcVatInPercent
      invoiceSummary._edit.vatInPercentWithLabel = `${calcVatInPercent}%`

      delete invoiceSummary._edit.items

      // Create items group by plan name
      const itemsGroupByPlanName = {}
      for (const item of invoiceSummary.items) {
        item.dateStart = moment(item.dateStart).format('MMM DD YYYY')
        item.value = (item.value / 100).toFixed(2)

        if (itemsGroupByPlanName[item.plan]) {
          itemsGroupByPlanName[item.plan].push(item)
        } else {
          itemsGroupByPlanName[item.plan] = []
          itemsGroupByPlanName[item.plan].push(item)
        }
      }

      invoiceSummary._edit.itemsGroupByPlanName = itemsGroupByPlanName

      return invoiceSummary
    },
    handleActionButtonClick (actionType) {
      if (actionType === 'confirm') {
        // If this component used by purchase plan then submit a request to the backend to update the plan
        if (this.accessorComponent === this.accessorComponentOptions.purchasePlan) {
          const apiRequest = () => {
            const requestData = { plan: this.planName.toLowerCase() }

            this.$utils.mainLoader(true)
            this.$api.put(`${CONFIG.API_BASE_URL}/group/plan`, requestData)
              .then(async (response) => {
                // Update the group store with the new plan information
                // which will trigger the watcher in auth.js plugin
                // to update signedInUserInfo in replacement for the
                // `BIND_USER_INFO_IN_VUE`, `USER_INFO_AVAILABLE_IN_VUE`
                // from EventBus.
                if (this.groupStore.groupInfo) {
                  this.groupStore.groupInfo.plans = response.data.plans
                }

                this.emitEvent('USER_PLAN_UPDATED', { switchedPlanTo: requestData.plan })
                this.$utils.mainLoader(false)
              })
              .catch((error) => {
                this.$utils.mainLoader(false)
                this.$apiError.handleUnauthorizedError(error, { originalRequest: () => { apiRequest() } })
              })
          }
          apiRequest()
        }

        // If this component used by setup player then only send confirm event
        if (this.accessorComponent === this.accessorComponentOptions.setup) {
          this.emitEvent('USER_CONFIRMED', this.invoiceSummary)
        }
      }

      if (actionType === 'cancel') {
        this.emitEvent('CANCEL_REVIEW_CONFIRMATION', {})
      }
    }
  },
  mounted () {
    this.setDefaultData()
  }
}
